(() => {
  const GLOBAL_KEY = '__yeahboxProductDesktopV2';
  const ROOT_SELECTOR = '[data-yeahbox-product-media]';
  const THUMB_SELECTOR = '[data-yeahbox-media-thumb]';
  const THUMB_EXPAND_SELECTOR = '[data-yeahbox-thumb-expand]';
  const DOT_SELECTOR = '[data-yeahbox-media-dot]';
  const desktopMediaQuery =
    typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 990px)') : null;

  const existingApi = window[GLOBAL_KEY];
  if (existingApi && typeof existingApi.refresh === 'function') {
    existingApi.refresh(document);
    return;
  }

  const controllers = new Map();

  const isDesktop = () => {
    return desktopMediaQuery ? desktopMediaQuery.matches : window.innerWidth >= 990;
  };

  const getIndexedValue = (element, fallbackIndex = 0) => {
    const candidates = [
      element?.getAttribute('data-yeahbox-media-thumb'),
      element?.getAttribute('data-yeahbox-media-dot'),
      element?.getAttribute('data-yeahbox-media-index'),
      element?.getAttribute('data-yeahbox-slide-index'),
    ];

    const matchedValue = candidates.find((value) => value !== null && value !== '' && Number.isFinite(Number(value)));
    return matchedValue === undefined ? fallbackIndex : Number(matchedValue);
  };

  const getGalleryTotal = (root, dots, thumbs) => {
    if (dots.length) return dots.length;

    const declaredTotal = Number(root.getAttribute('data-yeahbox-slide-count'));
    if (Number.isFinite(declaredTotal) && declaredTotal > 0) return declaredTotal;

    const slideTotal = root.querySelectorAll('[data-yeahbox-slide-index]').length;
    return slideTotal || thumbs.length;
  };

  const setThumbRailExpanded = (rail, expanded) => {
    if (!rail) return;
    const expandButton = rail.querySelector(THUMB_EXPAND_SELECTOR);
    const label = expandButton?.querySelector('[data-yeahbox-thumb-expand-label]');
    const isExpanded = Boolean(expanded);

    rail.classList.toggle('is-expanded', isExpanded);
    if (!expandButton) return;

    expandButton.setAttribute('aria-expanded', String(isExpanded));
    expandButton.setAttribute(
      'aria-label',
      isExpanded
        ? expandButton.dataset.yeahboxThumbShowFewer || ''
        : expandButton.dataset.yeahboxThumbShowMore || ''
    );
    if (label) {
      label.textContent = isExpanded ? label.dataset.expandedLabel || '−' : label.dataset.collapsedLabel || '';
    }
  };

  const keepActiveThumbVisible = (rail, activeThumb) => {
    if (!rail?.classList.contains('is-expanded') || !activeThumb) return;
    const thumbTop = activeThumb.offsetTop;
    const thumbBottom = thumbTop + activeThumb.offsetHeight;
    const visibleTop = rail.scrollTop;
    const visibleBottom = visibleTop + rail.clientHeight;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    if (thumbTop < visibleTop) {
      rail.scrollTo({ top: thumbTop, behavior });
    } else if (thumbBottom > visibleBottom) {
      rail.scrollTo({ top: thumbBottom - rail.clientHeight, behavior });
    }
  };

  const warmThumbRailImages = (rail) => {
    rail?.querySelectorAll('.yeahbox-anker-media__thumb-button--extra img').forEach((image) => {
      image.loading = 'eager';
      image.fetchPriority = 'low';
      if (typeof image.decode === 'function') image.decode().catch(() => {});
    });
  };

  const syncGalleryState = (root) => {
    const dots = Array.from(root.querySelectorAll(DOT_SELECTOR));
    const thumbs = Array.from(root.querySelectorAll(THUMB_SELECTOR));
    const thumbRail = root.querySelector('.yeahbox-anker-media__thumb-rail');
    const expandButton = thumbRail?.querySelector(THUMB_EXPAND_SELECTOR);
    const visibleThumbLimit = Number(thumbRail?.getAttribute('data-yeahbox-thumb-visible-limit')) || thumbs.length;
    const isThumbRailExpanded = thumbRail?.classList.contains('is-expanded') || false;
    const activeDot = dots.find((dot) => dot.classList.contains('is-active')) || dots[0];
    const activeDotPosition = activeDot ? dots.indexOf(activeDot) : 0;
    const activeIndex = activeDot ? getIndexedValue(activeDot, activeDotPosition) : 0;
    const total = getGalleryTotal(root, dots, thumbs);
    const current = total ? Math.min(activeDotPosition + 1, total) : 0;
    const progressPercentage = total ? `${(current / total) * 100}%` : '0%';

    let activeThumb = null;
    thumbs.forEach((thumb, thumbPosition) => {
      const thumbIndex = getIndexedValue(thumb, thumbPosition);
      const isActive = thumbIndex === activeIndex;

      thumb.classList.toggle('is-active', isActive);
      if (isActive) {
        activeThumb = thumb;
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }

      if (thumb.tagName === 'BUTTON') {
        thumb.setAttribute('aria-pressed', String(isActive));
      }
    });

    if (expandButton) {
      const representsHiddenActiveThumb = !isThumbRailExpanded && activeIndex >= visibleThumbLimit;
      expandButton.classList.toggle('is-active', representsHiddenActiveThumb);
      if (representsHiddenActiveThumb) {
        expandButton.setAttribute('aria-current', 'true');
      } else {
        expandButton.removeAttribute('aria-current');
      }
    }

    root.querySelectorAll('[data-yeahbox-media-step]').forEach((button) => {
      const step = Number(button.getAttribute('data-yeahbox-media-step'));
      const isDisabled = total <= 1 || (step < 0 ? activeIndex <= 0 : activeIndex >= total - 1);
      button.disabled = isDesktop() ? false : isDisabled;
      button.classList.toggle('is-disabled', isDisabled);
      button.setAttribute('aria-disabled', String(isDisabled));
    });

    root.querySelectorAll('[data-yeahbox-media-current]').forEach((element) => {
      element.textContent = String(current);
    });
    root.querySelectorAll('[data-yeahbox-media-total]').forEach((element) => {
      element.textContent = String(total);
    });
    root.querySelectorAll('[data-yeahbox-media-progress]').forEach((element) => {
      element.style.width = progressPercentage;
      element.style.setProperty('--yeahbox-media-progress', progressPercentage);
      element.setAttribute('role', 'progressbar');
      element.setAttribute('aria-valuemin', '0');
      element.setAttribute('aria-valuemax', String(total));
      element.setAttribute('aria-valuenow', String(current));
    });

    root.style.setProperty('--yeahbox-media-progress', progressPercentage);
    window.requestAnimationFrame(() => keepActiveThumbVisible(thumbRail, activeThumb));
  };

  const clickExistingGalleryControl = (root, thumb) => {
    const mediaId = thumb.getAttribute('data-yeahbox-media-id');
    if (mediaId) {
      const selectionWasHandled = !root.dispatchEvent(
        new CustomEvent('yeahbox:select-media', {
          detail: { mediaId },
          cancelable: true,
        })
      );
      if (selectionWasHandled) return;
    }

    const dots = Array.from(root.querySelectorAll(DOT_SELECTOR));
    const thumbs = Array.from(root.querySelectorAll(THUMB_SELECTOR));
    const thumbPosition = Math.max(thumbs.indexOf(thumb), 0);
    const requestedIndex = getIndexedValue(thumb, thumbPosition);
    const matchingDot =
      dots.find((dot, dotPosition) => getIndexedValue(dot, dotPosition) === requestedIndex) || dots[requestedIndex];

    if (matchingDot) {
      matchingDot.click();

      // The legacy gallery binds direct dot listeners only on its first root.
      // Keep later/section-rendered galleries usable without replacing that logic.
      if (!matchingDot.classList.contains('is-active')) {
        dots.forEach((dot) => dot.classList.toggle('is-active', dot === matchingDot));
        const track = root.querySelector('[data-yeahbox-media-track]');
        const slide = root.querySelector(`[data-yeahbox-slide-index="${requestedIndex}"]`);
        if (track && slide) {
          const left = Number.isFinite(slide.offsetLeft) ? slide.offsetLeft : track.clientWidth * requestedIndex;
          if (typeof track.scrollTo === 'function') {
            track.scrollTo({ left, behavior: 'smooth' });
          } else {
            track.scrollLeft = left;
          }
        }
      }

      syncGalleryState(root);
      return;
    }

    const track = root.querySelector('[data-yeahbox-media-track]');
    const slide = Array.from(root.querySelectorAll('[data-yeahbox-slide-index]')).find(
      (item, slidePosition) => getIndexedValue(item, slidePosition) === requestedIndex
    );
    if (!track || !slide) return;

    const left = Number.isFinite(slide.offsetLeft) ? slide.offsetLeft : track.clientWidth * requestedIndex;
    if (typeof track.scrollTo === 'function') {
      track.scrollTo({ left, behavior: 'smooth' });
    } else {
      track.scrollLeft = left;
    }
  };

  const createGalleryController = (root) => {
    const handleClick = (event) => {
      const expandButton = event.target?.closest?.(THUMB_EXPAND_SELECTOR);
      if (expandButton && root.contains(expandButton) && isDesktop()) {
        event.preventDefault();
        const thumbRail = expandButton.closest('.yeahbox-anker-media__thumb-rail');
        const isExpanding = !thumbRail?.classList.contains('is-expanded');
        setThumbRailExpanded(thumbRail, isExpanding);
        syncGalleryState(root);
        if (isExpanding) {
          warmThumbRailImages(thumbRail);
          window.requestAnimationFrame(() => {
            const firstExtraThumb = thumbRail?.querySelector('.yeahbox-anker-media__thumb-button--extra');
            if (!firstExtraThumb) return;
            firstExtraThumb.focus({ preventScroll: true });
            const targetTop = Math.max(
              firstExtraThumb.offsetTop + firstExtraThumb.offsetHeight - thumbRail.clientHeight,
              0
            );
            thumbRail.scrollTo({ top: targetTop, behavior: 'auto' });
          });
        }
        return;
      }

      const thumb = event.target?.closest?.(THUMB_SELECTOR);
      if (!thumb || !root.contains(thumb)) return;

      event.preventDefault();
      clickExistingGalleryControl(root, thumb);
    };

    const observer = new MutationObserver((mutations) => {
      const activeDotChanged = mutations.some(
        (mutation) => mutation.type === 'attributes' && mutation.target.matches?.(DOT_SELECTOR)
      );
      const dotListChanged = mutations.some((mutation) => {
        if (mutation.type !== 'childList') return false;
        return [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) => node.nodeType === 1 && (node.matches?.(DOT_SELECTOR) || node.querySelector?.(DOT_SELECTOR))
        );
      });

      if (activeDotChanged || dotListChanged) syncGalleryState(root);
    });

    root.addEventListener('click', handleClick);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
    syncGalleryState(root);

    return () => {
      root.removeEventListener('click', handleClick);
      observer.disconnect();
    };
  };

  const getRoots = (scope = document) => {
    const roots = [];
    if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
    if (typeof scope?.querySelectorAll === 'function') roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    return roots;
  };

  const pruneControllers = () => {
    controllers.forEach((destroy, root) => {
      if (root.isConnected) return;
      destroy();
      controllers.delete(root);
    });
  };

  const refresh = (scope = document) => {
    pruneControllers();

    getRoots(scope).forEach((root) => {
      if (controllers.has(root)) {
        syncGalleryState(root);
        return;
      }

      controllers.set(root, createGalleryController(root));
    });
  };

  const destroyWithin = (scope) => {
    controllers.forEach((destroy, root) => {
      if (root === scope || scope?.contains?.(root) || !root.isConnected) {
        destroy();
        controllers.delete(root);
      }
    });
  };

  const handleSectionLoad = (event) => {
    refresh(event.target || document);
  };

  const handleProductInfoLoaded = (event) => {
    refresh(event.target || document);
    window.setTimeout(pruneControllers, 750);
  };

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
  document.addEventListener('product-info:loaded', handleProductInfoLoaded);
  desktopMediaQuery?.addEventListener?.('change', () => refresh(document));

  const api = Object.freeze({ refresh });
  Object.defineProperty(window, GLOBAL_KEY, {
    configurable: true,
    value: api,
  });

  const boot = () => {
    refresh(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
